import {Brick, registerBrick, Service, ServiceRequestType} from 'olympe';
import {tap} from 'rxjs/operators';
import {
    applySubmitPrPeerSnapshots,
    collectPeerSnapshots,
    COLLECT_MEMORIES_TIMEOUT_MS,
    createSyncMutationLog,
    logSharedMemorySync,
    MAX_EXPECTED_REPLICAS,
    noteOwnMulticast,
    recordCollectReply,
    replaySubmitPrMutations,
    SYNC_ECHO_GRACE_MS,
    SYNC_MUTATION,
} from './sharedMemorySync';

/**
 * Shared memory of purchase requests currently being submitted.
 */
export default class CreateSubmitPRSharedMemory extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Array} inputs
     * @param {function()} forwardEvent
     */
    async update($, inputs, [forwardEvent]) {
        const backendId = crypto.randomUUID();

        const submitPRSharedMemory = new Service('submitPRSharedMemory', $);

        // This Set contains the tags of Purchase Requests that are currently being submitted.
        const prsBeingSubmitted = new Set();

        // Scatter-gather collections in progress. Keyed by correlation id.
        const pendingCollections = /** @type {Map<string, Object>} */ new Map();

        // Live add and delete operations recorded during startup sync.
        const startupMutations = createSyncMutationLog();
        startupMutations.start();

        /**
         * Snapshot of the PRs this backend currently has in flight.
         *
         * @return {{backendId: string, prsBeingSubmitted: Set<string>}}
         */
        const getSnapshot = () => ({
            backendId,
            prsBeingSubmitted,
        });

        /**
         * Ask every replica for its in-flight PRs and return the snapshots, this one included.
         *
         * @return {Promise<Map<string, Object>>}
         */
        const collectPeerPrs = () => collectPeerSnapshots({
            multicast: (body) => submitPRSharedMemory.multicast(body),
            requestBody: {topic: SUBMIT_PR_SHARED_MEMORY_TOPICS.COLLECT_MEMORIES},
            selfId: backendId,
            selfSnapshot: getSnapshot(),
            expected: MAX_EXPECTED_REPLICAS,
            timeoutMs: COLLECT_MEMORIES_TIMEOUT_MS,
            peerGraceMs: SYNC_ECHO_GRACE_MS,
            pendingCollections,
        });

        submitPRSharedMemory.listen().pipe(
            tap(async (req) => {
                const {topic,
                    uuid,
                    prTag,
                    correlationId,
                    coordinatorId,
                    memories,
                } = req.body();

                switch (req.requestType()) {
                case ServiceRequestType.GET:
                    switch (topic) {
                    case SUBMIT_PR_SHARED_MEMORY_TOPICS.PRS_BEING_SUBMITTED:
                        req.reply({prsBeingSubmitted: prsBeingSubmitted});
                        break;
                    case SUBMIT_PR_SHARED_MEMORY_TOPICS.PRS_BEING_SUBMITTED_HAS:
                        req.reply({has: prsBeingSubmitted.has(prTag)});
                        break;
                    }
                    break;
                case ServiceRequestType.PUBLISH:
                    if (uuid === backendId) {
                        noteOwnMulticast(pendingCollections, correlationId);
                        return;
                    }

                    switch (topic) {
                    case SUBMIT_PR_SHARED_MEMORY_TOPICS.COLLECT_MEMORIES:
                        // Reply to a collect request with this replica's set.
                        submitPRSharedMemory.multicast({
                            topic: SUBMIT_PR_SHARED_MEMORY_TOPICS.COLLECT_MEMORIES_REPLY,
                            uuid: backendId,
                            correlationId,
                            coordinatorId: uuid,
                            memories: getSnapshot(),
                        }).catch((e) => {
                            console.error('Error replying to the submit-PR memories collection request:', e);
                        });
                        return;
                    case SUBMIT_PR_SHARED_MEMORY_TOPICS.COLLECT_MEMORIES_REPLY:
                        recordCollectReply(pendingCollections, backendId, {correlationId, coordinatorId, uuid, memories});
                        return;
                    case SUBMIT_PR_SHARED_MEMORY_TOPICS.PRS_BEING_SUBMITTED_DELETE:
                        prsBeingSubmitted.delete(prTag);
                        startupMutations.record({op: SYNC_MUTATION.DELETE_PR, prTag});
                        break;
                    case SUBMIT_PR_SHARED_MEMORY_TOPICS.PRS_BEING_SUBMITTED_ADD:
                        prsBeingSubmitted.add(prTag);
                        startupMutations.record({op: SYNC_MUTATION.ADD_PR, prTag});
                        break;
                    }

                    if (!uuid) {
                        // Propagate this message to all backends to ensure everyone has a correct internal state
                        submitPRSharedMemory.multicast({...req.body(), uuid: backendId}).catch((e) => {
                            console.error('Error sharing message:', e);
                        });
                    }
                    break;
                }
            })).subscribe();

        try {
            const snapshots = await collectPeerPrs();
            // Get data from live replicas and apply the snapshots to this replica.
            // Ensure mutations happening during startup are replayed.
            const result = applySubmitPrPeerSnapshots(backendId, snapshots, prsBeingSubmitted);
            replaySubmitPrMutations(prsBeingSubmitted, startupMutations.drain());
            logSharedMemorySync('submitPRSharedMemory', result);
        } catch (e) {
            startupMutations.drain();
            console.error('Error synchronizing submit-PR shared memory on startup:', e);
            logSharedMemorySync('submitPRSharedMemory', {peerCount: 0, applied: 0});
        }

        forwardEvent();
    }
}

export const SUBMIT_PR_SHARED_MEMORY_TOPICS = {
    PRS_BEING_SUBMITTED: 'prs_being_submitted',
    PRS_BEING_SUBMITTED_HAS: 'prs_being_submitted_has',
    PRS_BEING_SUBMITTED_DELETE: 'prs_being_submitted_delete',
    PRS_BEING_SUBMITTED_ADD: 'prs_being_submitted_add',
    COLLECT_MEMORIES: 'collect_memories',
    COLLECT_MEMORIES_REPLY: 'collect_memories_reply',
};


registerBrick('01a0618116dbc85cb8bb', CreateSubmitPRSharedMemory);
